import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { motion } from 'framer-motion';

const IndependentKnowledgeSection = () => {
  const knowledgeTopics = [
    {
      id: 1,
      icon: "Calculator",
      title: "Kosten & terugverdientijd",
      description: "Bereken uw investering en wanneer deze zich terugverdient",
      path: "/costs-and-payback-period-guide"
    },
    {
      id: 2,
      icon: "Euro",
      title: "Subsidies & regelingen",
      description: "Overzicht van beschikbare subsidies en fiscale voordelen",
      path: "/subsidies-and-regulations-hub"
    },
    {
      id: 3,
      icon: "AlertCircle",
      title: "Veelgemaakte fouten",
      description: "Leer van de ervaringen van anderen en vermijd valkuilen",
      path: "/knowledge/mistakes"
    },
    {
      id: 4,
      icon: "CheckSquare",
      title: "Praktische beslisgidsen",
      description: "Stap-voor-stap hulp bij het maken van de juiste keuze",
      path: "/knowledge/guides"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-muted/30 relative overflow-hidden">
      {/* Animated background pattern */}
      <motion.div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: "radial-gradient(circle at 2px 2px, rgba(59, 130, 246, 0.15) 1px, transparent 0)",
          backgroundSize: "40px 40px"
        }}
        animate={{
          backgroundPosition: ["0px 0px", "40px 40px"],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear"
        }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          className="text-center mb-8 sm:mb-12 lg:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Icon name="BookOpen" size={24} className="text-primary" />
            <span className="text-sm font-caption font-medium text-primary">
              Onafhankelijke Kennis
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            Objectieve Informatie & Expert Advies
          </h2>
          <p className="text-lg font-body text-muted-foreground max-w-3xl mx-auto">
            Onze informatie is inhoudelijk, actueel en volledig onafhankelijk van aanbieders
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8 sm:mb-12"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {knowledgeTopics?.map((topic) => (
            <Link key={topic?.id} to={topic?.path} className="block">
              <motion.div
                variants={cardVariants}
                whileHover={{ 
                  scale: 1.08, 
                  y: -10,
                  rotateY: 5,
                  boxShadow: "0 20px 40px -10px rgba(0, 0, 0, 0.2), 0 0 20px rgba(59, 130, 246, 0.15)",
                  transition: { duration: 0.3 } 
                }}
                className="bg-card border border-border rounded-lg p-6 shadow-[0_8px_24px_-4px_rgba(0,0,0,0.12)] hover:shadow-[0_20px_40px_-10px_rgba(0,0,0,0.2)] transition-all cursor-pointer h-full transform perspective-1000"
                style={{ transformStyle: 'preserve-3d' }}
              >
                <motion.div 
                  className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4 shadow-[0_4px_12px_rgba(0,0,0,0.08)]"
                  whileHover={{ 
                    rotate: [0, -10, 10, -10, 10, 0],
                    scale: 1.15,
                    boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
                    z: 30
                  }}
                  transition={{ duration: 0.5 }}
                  style={{ transform: 'translateZ(20px)' }}
                >
                  <Icon name={topic?.icon} size={24} className="text-primary" />
                </motion.div>
                <motion.h3 
                  className="text-lg font-heading font-semibold text-foreground mb-2"
                  whileHover={{ color: "var(--primary)", x: 5 }}
                  style={{ transform: 'translateZ(10px)' }}
                >
                  {topic?.title}
                </motion.h3>
                <p className="text-sm font-body text-muted-foreground mb-4" style={{ transform: 'translateZ(5px)' }}>
                  {topic?.description}
                </p>
                <motion.div 
                  className="flex items-center text-primary text-sm font-body font-medium"
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <span>Lees meer</span>
                  <motion.div
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <Icon name="ArrowRight" size={14} className="ml-1" />
                  </motion.div>
                </motion.div>
              </motion.div>
            </Link>
          ))}
        </motion.div>

        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Link to="/knowledge">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button size="lg" iconName="Library" iconPosition="left">
                Bekijk alle verduurzamingskennis
              </Button>
            </motion.div>
          </Link>
        </motion.div>

        <motion.div 
          className="mt-12 bg-card border border-border rounded-lg p-6 max-w-4xl mx-auto"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          whileHover={{ scale: 1.01 }}
        >
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center">
                <Icon name="ShieldCheck" size={24} className="text-success" />
              </div>
            </div>
            <div>
              <h4 className="text-lg font-heading font-semibold text-foreground mb-2">
                Waarom onze kennis betrouwbaar is
              </h4>
              <p className="text-sm font-body text-muted-foreground leading-relaxed">
                Al onze artikelen worden geschreven door onafhankelijke energie-experts en regelmatig 
                geactualiseerd. We ontvangen geen commissies van aanbieders voor onze content, zodat u 
                verzekerd bent van objectieve informatie.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default IndependentKnowledgeSection;